import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    DeleteButton,
    Filter,
    TextInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useRecordContext,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Link, IconButton, Tooltip, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import { CheckCircle, Cancel, GetApp, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FirmwareFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.charger-firmware.list.filters.search')}
                source="q"
                alwaysOn
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const DownloadButton = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || !record.file_url) return null;

    return (
        <Tooltip title={translate('resources.charger-firmware.actions.download')}>
            <IconButton
                component="a"
                href={record.file_url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                    color: '#009A75',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 154, 117, 0.08)',
                    },
                }}
            >
                <GetApp fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

const StatusField = ({ record }: { record: any }) => {
    const translate = useTranslate();
    const isActive = record?.is_active;

    return (
        <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 16 }} /> : <Cancel sx={{ fontSize: 16 }} />}
            label={isActive ? translate('ra.boolean.true') : translate('ra.boolean.false')}
            size="small"
            sx={{
                backgroundColor: isActive ? '#E8F5E8' : '#EEEEEE',
                color: isActive ? '#009A75' : '#757575',
                fontWeight: 500,
                '& .MuiChip-icon': {
                    color: isActive ? '#009A75' : '#757575',
                },
            }}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/charger-firmware/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.vendor} - {record.model}
                            </Typography>
                            <Chip
                                label={`v${record.version}`}
                                size="small"
                                sx={{ flexShrink: 0, fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {record.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                    {record.description}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {formatFileSize(record.file_size || 0)}
                            </Typography>
                            <Chip
                                icon={record.is_active ? <CheckCircle sx={{ fontSize: 12 }} /> : <Cancel sx={{ fontSize: 12 }} />}
                                label={record.is_active ? translate('ra.boolean.true') : translate('ra.boolean.false')}
                                size="small"
                                sx={{
                                    flexShrink: 0,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    backgroundColor: record.is_active ? '#E8F5E8' : '#EEEEEE',
                                    color: record.is_active ? '#009A75' : '#757575',
                                    '& .MuiChip-icon': { color: record.is_active ? '#009A75' : '#757575' },
                                }}
                            />
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

const FirmwareList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<FirmwareFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'created_at', order: 'DESC' }}
                perPage={25}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': {
                            margin: 0,
                        },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .column-vendor': { width: '120px' },
                            '& .column-model': { width: '150px' },
                            '& .column-version': { width: '100px' },
                            '& .column-description': { width: '250px' },
                            '& .column-file_size': { width: '100px' },
                            '& .column-is_active': { width: '100px' },
                            '& .column-created_at': { width: '180px' },
                            '& .column-actions': { width: '120px' },
                        }}
                    >
                        <TextField
                            source="vendor"
                            label={translate('resources.charger-firmware.fields.vendor')}
                        />
                        <TextField
                            source="model"
                            label={translate('resources.charger-firmware.fields.model')}
                            sx={{ fontWeight: 500 }}
                        />
                        <FunctionField
                            label={translate('resources.charger-firmware.fields.version')}
                            render={(record: any) => `v${record.version}`}
                            source="version"
                            sx={{ fontWeight: 600 }}
                        />
                        <TextField
                            source="description"
                            label={translate('resources.charger-firmware.fields.description')}
                        />
                        <FunctionField
                            label={translate('resources.charger-firmware.fields.file_size')}
                            render={(record: any) => formatFileSize(record.file_size || 0)}
                            source="file_size"
                        />
                        <FunctionField
                            label={translate('resources.charger-firmware.fields.is_active')}
                            render={(record: any) => <StatusField record={record} />}
                            source="is_active"
                        />
                        <DateField
                            source="created_at"
                            label={translate('resources.charger-firmware.fields.uploaded_at')}
                            locales="en-GB"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }}
                        />
                        <FunctionField
                            label=""
                            render={(record: any) => (
                                <Box display="flex" gap={0.5} alignItems="center">
                                    <DownloadButton />
                                    <EditButton record={record} label="" />
                                    <DeleteButton record={record} label="" />
                                </Box>
                            )}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/charger-firmware/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default FirmwareList;
